import { useMemo, useState } from "react";
import { useDelete, useGetList, useNotify, useRefresh, useTranslate, type Identifier } from "ra-core";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DateField } from "@/components/admin/date-field";

import type { CampaignTaskDefinition, CampaignTaskTarget } from "../types";
import { useIsCampaignAdmin } from "./useIsCampaignAdmin";
import { getCampaignTaskTypeConfig } from "./campaignTaskTypes";
import { CampaignTaskDefinitionForm } from "./CampaignTaskDefinitionForm";
import { GenerateTasksDialog } from "./GenerateTasksDialog";

export const CampaignTaskDefinitionList = ({
  campaignId,
}: {
  campaignId: Identifier;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const isAdmin = useIsCampaignAdmin();
  const [editingId, setEditingId] = useState<Identifier | null>(null);
  const [creating, setCreating] = useState(false);
  const [generateId, setGenerateId] = useState<Identifier | null>(null);
  const [deleteOne] = useDelete();

  const { data: definitions, isPending } = useGetList<CampaignTaskDefinition>(
    "campaign_task_definitions",
    {
      filter: { campaign_id: campaignId },
      pagination: { page: 1, perPage: 100 },
      sort: { field: "created_at", order: "ASC" },
    },
  );

  const { data: campaignTargets } = useGetList("campaign_targets", {
    filter: { campaign_id: campaignId },
    pagination: { page: 1, perPage: 500 },
  });

  const definitionIds = useMemo(
    () => (definitions ?? []).map((d) => d.id),
    [definitions],
  );

  const { data: targets } = useGetList<CampaignTaskTarget>(
    "campaign_task_targets",
    {
      filter: {
        "campaign_task_definition_id@in": `(${definitionIds.join(",")})`,
      },
      pagination: { page: 1, perPage: 500 },
    },
    { enabled: definitionIds.length > 0 },
  );

  const targetsByDefinition = useMemo(() => {
    const map = new Map<Identifier, CampaignTaskTarget[]>();
    targets?.forEach((t) => {
      const list = map.get(t.campaign_task_definition_id) ?? [];
      list.push(t);
      map.set(t.campaign_task_definition_id, list);
    });
    return map;
  }, [targets]);

  const campaignTargetCount = campaignTargets?.length ?? 0;

  if (isPending) return null;

  return (
    <div className="flex flex-col gap-4">
      {isAdmin && (
        <div className="flex justify-end">
          <Button type="button" onClick={() => setCreating(true)}>
            {translate("resources.campaigns.task_definitions.add", {
              _: "Add task definition",
            })}
          </Button>
        </div>
      )}

      {definitions?.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {translate("resources.campaigns.task_definitions.empty", {
            _: "No task definitions yet.",
          })}
        </p>
      )}

      {definitions?.map((def) => {
        const config = getCampaignTaskTypeConfig(def.kind);
        const defTargets = targetsByDefinition.get(def.id) ?? [];
        const inherits = def.inherit_campaign_targets === true;
        const targetCount = inherits ? campaignTargetCount : defTargets.length;
        return (
          <Card key={def.id}>
            <CardHeader className="flex flex-row items-start justify-between gap-4 pb-2">
              <div>
                <CardTitle className="text-base">{def.title}</CardTitle>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Badge variant="secondary">{config?.label ?? def.kind}</Badge>
                  {inherits && (
                    <Badge variant="outline">
                      {translate("resources.campaigns.targets.inherited_badge", {
                        _: "Inherited from campaign",
                      })}
                    </Badge>
                  )}
                  <Badge variant="outline">
                    {targetCount}{" "}
                    {translate("resources.campaigns.task_definitions.targets", {
                      _: "targets",
                    })}
                  </Badge>
                  {def.last_generated_at && (
                    <Badge variant="outline">
                      {translate("resources.campaigns.task_definitions.generated", {
                        _: "Generated",
                      })}
                    </Badge>
                  )}
                </div>
              </div>
              {isAdmin && (
                <div className="flex gap-2 shrink-0">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingId(def.id)}
                  >
                    {translate("ra.action.edit")}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => setGenerateId(def.id)}
                  >
                    {def.last_generated_at
                      ? translate("resources.campaigns.generate.reconcile", {
                          _: "Reconcile tasks",
                        })
                      : translate("resources.campaigns.generate.action", {
                          _: "Generate tasks",
                        })}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      deleteOne(
                        "campaign_task_definitions",
                        { id: def.id, previousData: def },
                        {
                          onSuccess: () => {
                            notify("ra.notification.deleted", {
                              type: "info",
                              messageArgs: { smart_count: 1 },
                            });
                            refresh();
                          },
                        },
                      )
                    }
                  >
                    {translate("ra.action.delete")}
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-2">
              {def.text && <p>{def.text}</p>}
              <p>
                {translate("resources.tasks.fields.due_date")}:{" "}
                <DateField source="due_date" record={def} showDate showTime />
              </p>
            </CardContent>
          </Card>
        );
      })}

      {(creating || editingId != null) && (
        <CampaignTaskDefinitionForm
          campaignId={campaignId}
          definitionId={editingId ?? undefined}
          initialTargets={
            editingId != null ? targetsByDefinition.get(editingId) : undefined
          }
          onClose={() => {
            setCreating(false);
            setEditingId(null);
            refresh();
          }}
        />
      )}

      {generateId != null && (
        <GenerateTasksDialog
          definitionId={generateId}
          reconcile={!!definitions?.find((d) => d.id === generateId)?.last_generated_at}
          onClose={() => {
            setGenerateId(null);
            refresh();
          }}
        />
      )}
    </div>
  );
};
